import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { MailOpen, Users } from "lucide-react";
import {
  useAcceptInvitation,
  useGetGroupedInvitations,
  useRejectInvitation,
} from "../../../api/hooks";
import { useAcceptAndSwitch } from "../../../hooks/use-accept-and-switch";
import { InvitationCard } from "../../my-invitations/invitation-card";
import { InvitationCardSkeleton } from "../../my-invitations/invitation-card-skeleton";

type InvitationsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function InvitationsModal({
  open,
  onOpenChange,
}: InvitationsModalProps) {
  const { data, isFetching } = useGetGroupedInvitations();
  const { mutate: acceptInvitation, isPending: isAcceptingInvitation } =
    useAcceptInvitation();
  const { mutate: declineInvitation, isPending: isDecliningInvitation } =
    useRejectInvitation();
  const { acceptAndSwitch, isPending: isAcceptingAndSwitching } =
    useAcceptAndSwitch({
      onSuccess: () => {
        onOpenChange(false);
      },
    });

  const groupedInvitations = data.invitations;
  const invitationCount = data.invitationCount;
  const isBusy = isFetching;

  const getModalTitle = () => {
    if (isBusy) {
      return "Invitations";
    }
    if (invitationCount === 1) {
      return "Project Invitation";
    }
    if (invitationCount > 1) {
      return "Project Invitations";
    }
    return "Invitations";
  };

  const handleAccept = (invitationId?: string) => {
    if (!invitationId) {
      return;
    }
    acceptInvitation({ invitationId });
    if (invitationCount <= 1) {
      onOpenChange(false);
    }
  };

  const handleAcceptAndSwitch = (projectId: string) => {
    // Find the invitation
    const invitation = groupedInvitations.find(
      (inv) => inv.project_id === projectId
    );

    if (!invitation) {
      return;
    }

    acceptAndSwitch(invitation);
  };

  const handleDecline = (invitationId?: string) => {
    if (!invitationId) {
      return;
    }
    declineInvitation({ invitationId });
    if (invitationCount <= 1) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="flex max-h-[85vh] w-full max-w-lg flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        {/* Header */}
        <div className="border-b bg-muted/30">
          <DialogHeader className="p-6 pb-5">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <MailOpen className="h-6 w-6 text-brand-text" />
              </div>

              <div className="min-w-0 flex-1 space-y-1.5">
                <DialogTitle className="text-xl">{getModalTitle()}</DialogTitle>
                <DialogDescription className="text-sm leading-relaxed">
                  {invitationCount === 1
                    ? "You've been invited to join a project! Accept to start collaborating with your team."
                    : `You have ${invitationCount} project invitations waiting. Join your teams and start collaborating!`}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Content area */}
        <div className="max-h-[calc(85vh-140px)] flex-1 overflow-y-auto">
          {/* Loading state with skeletons */}
          {!!isBusy && (
            <div className="space-y-4 p-6">
              <InvitationCardSkeleton />
              <InvitationCardSkeleton />
            </div>
          )}

          {/* Empty state */}
          {!isBusy && invitationCount === 0 && (
            <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted/50">
                <Users className="h-8 w-8 text-muted-foreground/60" />
              </div>
              <h3 className="mb-2 font-medium text-foreground">
                No Invitations Yet
              </h3>
              <p className="max-w-sm text-muted-foreground text-sm">
                When teammates invite you to collaborate on their projects,
                you&apos;ll see those invitations here.
              </p>
            </div>
          )}

          {/* Invitations list */}
          {!isBusy && invitationCount > 0 && (
            <div className="space-y-4 p-6">
              {groupedInvitations.map((invitation) => (
                <InvitationCard
                  invitation={invitation}
                  isAccepting={isAcceptingInvitation || isAcceptingAndSwitching}
                  isDeclining={isDecliningInvitation}
                  key={invitation.project_id}
                  onAccept={handleAccept}
                  onAcceptAndSwitch={handleAcceptAndSwitch}
                  onDecline={handleDecline}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
